import { useCallback, useEffect, useRef, useState } from 'react';

import type { TrackingEventProperties } from '@change-corgi/core/eventTracker';

import { useDmScoreData } from '../../../../hooks/useDmScoreData';
import { useTrackHandlers } from '../../../../hooks/useTrackHandlers';
import type { ResponsivenessTags } from '../../../../utils/getScoreTag';
import { useContactDetails } from '../useContactDetails';

type Result = ModelHookResult<
	{
		isTagExpanded: boolean;
		responsivenessRef: React.RefObject<HTMLDivElement>;
		expandButtonAriaLabel: string;
		responsivenessScore: number | null;
		scoreTag:
			| {
					label: string;
					labelColor: string;
					textColor: string;
			  }
			| undefined;
		tags: ResponsivenessTags;
		totalRelevantPetitions: number | null;
		respondedToPetitions: number | null;
		hasCountValues: boolean;
		contactDetails: {
			email: string;
			phone: string;
			emailHref: string;
		};
	},
	{ toggleTagExpansion: () => void; trackEmailLinkClick: (options?: TrackingEventProperties) => void }
>;

export function useCalculatingResponsiveness(): Result {
	const [isTagExpanded, setIsTagExpanded] = useState(false);
	const responsivenessRef = useRef<HTMLDivElement>(null);

	const { data: dmScoreData } = useDmScoreData();
	const { data: contactDetails } = useContactDetails();

	const {
		actions: { trackTagExpansion, trackEmailLinkClick },
	} = useTrackHandlers();

	useEffect(() => {
		const handleClick = (event: MouseEvent | FocusEvent) => {
			if (responsivenessRef.current && !responsivenessRef.current.contains(event.target as Node)) {
				setIsTagExpanded(false);
			}
		};

		document.addEventListener('click', handleClick);
		document.addEventListener('focusin', handleClick);

		return () => {
			document.removeEventListener('click', handleClick);
			document.removeEventListener('focusin', handleClick);
		};
	}, []);

	const toggleTagExpansion = useCallback(() => {
		setIsTagExpanded(!isTagExpanded);
		trackTagExpansion({
			action_type: isTagExpanded ? 'close' : 'open',
		});
	}, [isTagExpanded, trackTagExpansion]);

	const labelBase = 'How do we calculate this?';
	const expandButtonAriaLabel = isTagExpanded ? `${labelBase} Click to collapse` : `${labelBase} Click to expand`;

	return {
		data: {
			responsivenessRef,
			isTagExpanded,
			expandButtonAriaLabel,
			...dmScoreData,
			contactDetails,
		},
		actions: { toggleTagExpansion, trackEmailLinkClick },
	};
}
